import { Gavel, ShieldCheck, ScrollText, BellRing, UserCheck } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/MultiSelect";
import { ChannelPicker } from "@/components/panel/ChannelPicker";

interface Props {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: GuildConfig["moderation"]) => void;
}

export function ModerationTab({ config, channels, roles, onChange }: Props) {
  const m = config.moderation;
  const update = <K extends keyof typeof m>(k: K, v: (typeof m)[K]) =>
    onChange({ ...m, [k]: v });

  const text = channels.filter((ch) => ch.type === 0);

  return (
    <Section
      icon={Gavel}
      title="Moderation Center"
      description="Professional-grade tools to keep your community safe. Manage warnings, kicks, bans, and more."
      enabled={m.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="grid gap-6">
        {/* Permission Setup */}
        <Card className="p-5 border-border/50 bg-muted/20 space-y-4">
          <div className="flex items-center gap-2 font-semibold text-sm text-primary">
            <UserCheck className="h-4 w-4" />
            Staff & Permissions
          </div>
          <Field
            label="Moderator Roles"
            hint="Users with these roles can use commands like /warn, /kick, /ban, and /mute."
          >
            <MultiSelect
              options={roles.map(r => ({ id: r.id, name: r.name, color: r.color }))}
              value={m.mod_role_ids}
              onChange={(v) => update("mod_role_ids", v)}
              prefix="@"
              placeholder="+ add mod role"
            />
          </Field>
        </Card>

        {/* Action Logs */}
        <Card className="p-5 border-border/50 bg-muted/20 space-y-4">
          <div className="flex items-center gap-2 font-semibold text-sm text-primary">
            <ScrollText className="h-4 w-4" />
            Moderation Logging
          </div>
          <Field
            label="Mod-Log Channel"
            hint="Where the bot will post all moderation actions (warns, bans, etc.)"
          >
            <ChannelPicker
              channels={text}
              value={m.log_channel_id}
              onChange={(v) => update("log_channel_id", v === "__none__" ? "" : v)}
            />
          </Field>
        </Card>

        {/* Notifications */}
        <Card className="p-5 border-border/50 bg-muted/20 space-y-4">
          <div className="flex items-center gap-2 font-semibold text-sm text-primary">
            <BellRing className="h-4 w-4" />
            User Notifications
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">DM User on Action</div>
              <div className="text-xs text-muted-foreground">
                Automatically send a private message to the user explaining why they were warned/kicked/banned.
              </div>
            </div>
            <Switch
              checked={m.dm_on_action}
              onCheckedChange={(v) => update("dm_on_action", v)}
            />
          </div>
        </Card>

        {/* Command Quick List */}
        <Card className="p-5 border-border/50 bg-muted/20">
          <div className="flex items-center gap-2 font-semibold text-sm text-primary mb-3">
            <ShieldCheck className="h-4 w-4" />
            Available Commands (English)
          </div>
          <div className="grid sm:grid-cols-2 gap-2 text-xs font-mono text-muted-foreground">
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/warn</span> [user] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/mute</span> [user] [duration] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/unmute</span> [user] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/kick</span> [user] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/ban</span> [user] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/unban</span> [user_id] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/softban</span> [user] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/purge</span> [amount]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/lock</span> [channel] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/unlock</span> [channel] [reason]
            </div>
            <div className="p-2 rounded bg-background/50 border border-border/30">
              <span className="text-primary">/slowmode</span> [seconds] [channel] [reason]
            </div>
          </div>
        </Card>
      </div>
    </Section>
  );
}
